import { useRef, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from '@/components/layout/Container';
import { Section } from '@/components/layout/Section';
import { Eyebrow } from '@/components/domain/Eyebrow';
import { AnimatedNumber } from '@/components/domain/AnimatedNumber';
import { useFadeUpOnScroll } from '@/hooks/useFadeUpOnScroll';
import styles from './StatsSection.module.css';

export const StatsSection: FC = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  useFadeUpOnScroll(ref, { selector: '[data-fade]', stagger: 0.06 });

  const items = t('home.stats.items', { returnObjects: true }) as Array<{
    value: number;
    suffix: string;
    prefix?: string;
    label: string;
  }>;

  // console.log('[StatsSection] rendered, items count:', items.length, items);

  // Module-level debug: verify this section actually renders.
  // console.log('[DEBUG] StatsSection JSX reached');

  return (
    <Section tone="dark" id="stats">
      <Container size="default">
        <div ref={ref}>
          <header className={styles.header}>
            <div data-fade>
              <Eyebrow tone="inverse">{t('home.stats.eyebrow')}</Eyebrow>
            </div>
            <h2 className={styles.title} data-fade>
              {t('home.stats.title')}
            </h2>
          </header>
          <ul className={styles.grid}>
            {items.map((item, idx) => (
              <li key={`${item.label}-${idx}`} className={styles.cell} data-fade>
                <AnimatedNumber
                  value={item.value}
                  prefix={item.prefix}
                  suffix={item.suffix}
                  decimals={item.value % 1 !== 0 ? 1 : 0}
                />
                <p className={styles.label}>{item.label}</p>
              </li>
            ))}
          </ul>
        </div>
      </Container>
    </Section>
  );
};

StatsSection.displayName = 'StatsSection';
